import React, { useEffect, useState } from "react";
import { Loader2, Trophy, Lightbulb } from "lucide-react";

const tone = (s) => (s >= 80 ? "text-emerald-700 bg-emerald-50 ring-emerald-200" : s >= 60 ? "text-amber-700 bg-amber-50 ring-amber-200" : "text-red-700 bg-red-50 ring-red-200");
const bar = (r) => (r >= 0.8 ? "bg-emerald-500" : r >= 0.6 ? "bg-amber-500" : "bg-red-500");

export const lastMonths = (n = 6) => {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

export const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });

export const MonthPicker = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid={testid}>
    {lastMonths().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
  </select>
);

export const ScoreBadge = ({ score, testid }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-lg font-extrabold ring-1 ${tone(score)}`} data-testid={testid}>{score}<span className="text-xs font-semibold ml-0.5">/100</span></span>
);

export const PartsList = ({ parts, prefix }) => (
  <div className="divide-y divide-slate-100">
    {parts.map((p) => (
      <div key={p.key} className="py-2.5" data-testid={`${prefix}-part-${p.key}`}>
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="font-semibold text-slate-800">{p.label}</span>
          <span className="font-mono text-slate-700">{p.applies ? `${p.earned} / ${p.points}` : "—"}</span>
        </div>
        {p.applies && <div className="mt-1 h-1.5 rounded-full bg-slate-100"><div className={`h-1.5 rounded-full ${bar(p.earned / p.points)}`} style={{ width: `${(100 * p.earned) / p.points}%` }} /></div>}
        <p className="mt-1 text-xs text-slate-500">{p.detail}</p>
      </div>
    ))}
  </div>
);

export default function MyScore({ fetcher }) {
  const [month, setMonth] = useState(lastMonths()[0]);
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); fetcher(month).then(setD).catch(() => setD({ error: true })); }, [month, fetcher]);
  return (
    <div className="max-w-3xl space-y-4" data-testid="my-score">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h2 className="text-lg font-bold text-slate-900">My Scorecard</h2><p className="text-sm text-slate-500">Your monthly score out of 100, calculated automatically from your daily work.</p></div>
        <MonthPicker value={month} onChange={setMonth} testid="my-score-month" />
      </div>
      {!d ? <Loader2 className="h-5 w-5 animate-spin text-slate-500" /> : d.error ? <p className="text-sm text-red-600">Could not load your score.</p> : (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-4">
            <ScoreBadge score={d.score} testid="my-score-total" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-sm font-bold text-blue-800 ring-1 ring-blue-200" data-testid="my-score-rank"><Trophy className="h-4 w-4" /> Rank {d.rank} of {d.of}</span>
            <span className="text-sm text-slate-500">{d.students} active students · {monthLabel(d.month)}</span>
          </div>
          {d.tips?.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="my-score-tips">
              <p className="flex items-center gap-1.5 text-sm font-bold text-amber-800"><Lightbulb className="h-4 w-4" /> How to score higher</p>
              <ul className="mt-1 list-disc pl-5 text-sm text-amber-900 space-y-0.5">{d.tips.map((t) => <li key={t}>{t}</li>)}</ul>
            </div>
          )}
          <div className="rounded-2xl border border-slate-200 bg-white px-4"><PartsList parts={d.parts} prefix="my-score" /></div>
          <p className="text-xs text-slate-400">Parts marked “—” don't apply to you this month and are left out; your score is scaled to 100 from the parts that apply.</p>
        </>
      )}
    </div>
  );
}
